import React, { useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest } from "../helpers/api";
import { logout } from "../helpers/authHelper";
import { showError, showSuccess } from "../components/ToastNotifications";
import AuthShowcase, { AuthStatus, PasswordField, PasswordStrength, SubmitButton, validateNewPassword } from "../components/auth/AuthShowcase";

const tokenPattern = /^[a-f0-9]{64}$/i;
const clearTokenFromAddressBar = () => window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`);

const ResetPassword = () => {
  // The token travels in the URL fragment, so it is never sent to servers or logged in access logs.
  const [token] = useState(() => window.location.hash.slice(1));
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(tokenPattern.test(token) ? "form" : "invalid");

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    const nextErrors = validateNewPassword(password, confirmPassword);
    setErrors(nextErrors);
    const firstProblem = ["password", "confirmPassword"].find((field) => nextErrors[field]);
    if (firstProblem) { document.getElementById(firstProblem === "password" ? "reset-password" : "confirm-reset-password")?.focus(); return; }
    setSubmitting(true);
    try {
      const result = await apiRequest("/api/auth/reset-password", { method: "POST", body: JSON.stringify({ token, password }) });
      clearTokenFromAddressBar();
      // Signing in again with the new password replaces any session left in this browser.
      logout();
      setStatus("done");
      showSuccess(result.message || "Your password has been updated.");
    } catch (requestError) {
      if (requestError.status === 400 && /link/i.test(requestError.message)) {
        clearTokenFromAddressBar();
        setStatus("expired");
      } else setError(requestError.message);
      showError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const linkProblem = status === "invalid" || status === "expired";

  return (
    <AuthShowcase
      eyebrow="Account recovery"
      title={<>A fresh password, <em>a fresh start.</em></>}
      intro="Choose a strong new password to keep your posts, calendar and customer messages secure."
    >
      {status === "done" ? <div className="auth-state">
        <AuthStatus tone="success" icon="ti-shield-check" />
        <h2>Password updated</h2>
        <p>Your password has been changed. Sign in with your new password to continue.</p>
        <Link className="auth-submit" to="/login">Continue to sign in <i className="ti ti-arrow-right" aria-hidden="true" /></Link>
      </div> : linkProblem ? <div className="auth-state">
        <AuthStatus tone="error" icon="ti-link-off" />
        <h2>{status === "expired" ? "This link has expired" : "This link isn't valid"}</h2>
        <p>{status === "expired" ? "Password reset links work once and expire after a short time." : "The reset link is missing or incomplete. Open the newest link from your email, or request a new one."}</p>
        <Link className="auth-submit" to="/forgot-password"><i className="ti ti-mail-forward" aria-hidden="true" /> Request a new link</Link>
        <p className="auth-switch"><Link className="auth-link" to="/login">Back to sign in</Link></p>
      </div> : <>
        <header className="auth-card-head">
          <h2>Create a new password</h2>
          <p>Use at least 10 characters, including a letter and a number.</p>
        </header>
        <form className="auth-form" onSubmit={submit} noValidate>
          {error && <div className="auth-alert" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" /><span>{error}</span></div>}
          <div className="auth-field-group">
            <PasswordField id="reset-password" label="New password" autoComplete="new-password" placeholder="Create a new password" maxLength={72} autoFocus
              value={password} error={errors.password} describedBy="reset-password-strength"
              onChange={(event) => { setPassword(event.target.value); if (errors.password) setErrors((current) => ({ ...current, password: "" })); }} required />
            <PasswordStrength id="reset-password-strength" password={password} />
          </div>
          <PasswordField id="confirm-reset-password" label="Confirm new password" autoComplete="new-password" placeholder="Repeat your new password" maxLength={72}
            value={confirmPassword} error={errors.confirmPassword}
            onChange={(event) => { setConfirmPassword(event.target.value); if (errors.confirmPassword) setErrors((current) => ({ ...current, confirmPassword: "" })); }}
            onBlur={() => confirmPassword && setErrors((current) => ({ ...current, confirmPassword: validateNewPassword(password, confirmPassword).confirmPassword }))} required />
          <SubmitButton busy={submitting} busyLabel="Updating password…">Reset password</SubmitButton>
        </form>
        <p className="auth-switch">Link not working? <Link className="auth-link" to="/forgot-password">Request a new one</Link></p>
      </>}
    </AuthShowcase>
  );
};

export default ResetPassword;
